import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Pagination from '../../components/Pagination';
import { useSettings } from '../../context/SettingsContext';

const METHOD_ICONS = { cash: '💵', card: '💳', transfer: '🏦' };
const PAGE_SIZES   = [10, 25, 50];

const fmt = (n) => (n != null && !isNaN(n) ? Number(n).toLocaleString() : '0');

function StatusBadge({ status, t }) {
  if (status === 'paid')    return <span className="badge badge-green">✓ {t('paid')}</span>;
  if (status === 'partial') return <span className="badge" style={{ background: 'rgba(201,168,76,0.15)', color: 'var(--accent)' }}>◑ {t('partial')}</span>;
  return <span className="badge badge-red">✗ {t('unpaid')}</span>;
}

export default function PartnerRestaurantDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const { t }    = useSettings();

  const [restaurant,    setRestaurant]    = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [calendar,      setCalendar]      = useState([]);
  const [calYear,       setCalYear]       = useState(new Date().getFullYear());
  const [tab,           setTab]           = useState('history');
  const [loading,       setLoading]       = useState(true);
  const [page,          setPage]          = useState(1);
  const [pageSize,      setPageSize]      = useState(10);

  useEffect(() => { setPage(1); }, [tab, pageSize]);

  const fetchAll = useCallback(async () => {
    try {
      const [rRes, sRes, cRes] = await Promise.all([
        axios.get(`/api/partner/restaurants/${id}`),
        axios.get(`/api/partner/restaurants/${id}/subscriptions`),
        axios.get(`/api/partner/restaurants/${id}/calendar?year=${calYear}`),
      ]);
      setRestaurant(rRes.data);
      setSubscriptions(sRes.data);
      setCalendar(cRes.data);
    } catch (err) {
      if (err.response?.status === 404) navigate('/partner');
    } finally {
      setLoading(false);
    }
  }, [id, calYear, navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const months      = t('months');
  const monthsShort = t('monthsShort');

  const totalPages = Math.max(1, Math.ceil(subscriptions.length / pageSize));
  const pagedSubs  = useMemo(
    () => subscriptions.slice((page - 1) * pageSize, page * pageSize),
    [subscriptions, page, pageSize]
  );

  const calColor = (s) => {
    if (s === 'paid')    return { bg: 'var(--green-bg)',         border: 'rgba(76,175,130,0.25)',  dot: 'var(--green)' };
    if (s === 'partial') return { bg: 'rgba(201,168,76,0.1)',   border: 'rgba(201,168,76,0.3)',   dot: 'var(--accent)' };
    if (s === 'overdue') return { bg: 'var(--red-bg)',           border: 'rgba(224,92,92,0.25)',   dot: 'var(--red)' };
    return               { bg: 'var(--gray-bg)',          border: 'var(--border)',           dot: 'var(--text-dim)' };
  };

  if (loading) return <div style={{ padding: 40, color: 'var(--text-muted)' }}>{t('loading')}</div>;
  if (!restaurant) return null;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <button onClick={() => navigate('/partner')} style={{ background: 'none', color: 'var(--text-muted)', fontSize: 13, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
          ← {t('back')}
        </button>
        <h1 style={{ fontSize: 30, marginBottom: 8 }}>{restaurant.name}</h1>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          {restaurant.planId && <span className="badge badge-gold">{restaurant.planId.name || restaurant.planId}</span>}
          {restaurant.phoneNumber && <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>📞 {restaurant.phoneNumber}</span>}
          {restaurant.address && <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>📍 {restaurant.address}</span>}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)', overflowX: 'auto' }}>
        {[['history', t('paymentHistory')], ['calendar', t('calendarView')]].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            background: 'none', whiteSpace: 'nowrap',
            color: tab === key ? 'var(--accent)' : 'var(--text-muted)',
            padding: '10px 18px', fontSize: 14, fontWeight: tab === key ? 600 : 400,
            borderBottom: tab === key ? '2px solid var(--accent)' : '2px solid transparent',
            marginBottom: -1, transition: 'all 0.15s',
          }}>{label}</button>
        ))}
      </div>

      {/* History Tab */}
      {tab === 'history' && (
        <div className="card" style={{ padding: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('rowsPerPage')}:</span>
            <select className="select-control" value={pageSize} onChange={e => setPageSize(Number(e.target.value))} style={{ width: 70 }}>
              {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          {subscriptions.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>{t('noPayments')}</div>
          ) : (
            <div className="table-scroll">
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Period', t('paymentStatus'), t('paidAmount'), t('totalAmount'), t('remainingAmount'), t('paymentMethod'), t('date')].map((h, i) => (
                      <th key={i} style={{ padding: '12px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagedSubs.map(s => (
                    <tr key={s._id} style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.12s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--table-hover)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '12px 14px', fontWeight: 500, whiteSpace: 'nowrap' }}>{months[s.month - 1]} {s.year}</td>
                      <td style={{ padding: '12px 14px' }}><StatusBadge status={s.paymentStatus} t={t} /></td>
                      <td style={{ padding: '12px 14px', color: 'var(--green)', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmt(s.paidAmount)} UZS</td>
                      <td style={{ padding: '12px 14px', color: 'var(--text-muted)', fontSize: 13, whiteSpace: 'nowrap' }}>{fmt(s.totalAmount)} UZS</td>
                      <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                        <span style={{ color: s.remainingAmount > 0 ? 'var(--red)' : 'var(--text-dim)', fontSize: 13 }}>
                          {s.remainingAmount > 0 ? fmt(s.remainingAmount) + ' UZS' : '—'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 13, whiteSpace: 'nowrap' }}>{METHOD_ICONS[s.paymentMethod]} {t(s.paymentMethod)}</td>
                      <td style={{ padding: '12px 14px', color: 'var(--text-muted)', fontSize: 13, whiteSpace: 'nowrap' }}>{new Date(s.paidAt).toLocaleDateString('en-GB')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pagination page={page} totalPages={totalPages} total={subscriptions.length} pageSize={pageSize} onPage={setPage} />
        </div>
      )}

      {/* Calendar Tab */}
      {tab === 'calendar' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <button className="btn-ghost" style={{ padding: '7px 13px', fontSize: 13 }} onClick={() => setCalYear(y => y - 1)}>← {calYear - 1}</button>
            <span style={{ fontFamily: 'DM Serif Display', fontSize: 20 }}>{calYear}</span>
            <button className="btn-ghost" style={{ padding: '7px 13px', fontSize: 13 }} onClick={() => setCalYear(y => y + 1)}>{calYear + 1} →</button>
          </div>
          <div className="calendar-grid">
            {calendar.map(({ month, status, subscription: sub }) => {
              const c = calColor(status);
              return (
                <div key={month} style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 'var(--radius)', padding: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{monthsShort[month - 1]}</span>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.dot, display: 'inline-block' }} />
                  </div>
                  {sub ? (
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2, color: sub.paymentStatus === 'paid' ? 'var(--green)' : sub.paymentStatus === 'partial' ? 'var(--accent)' : 'var(--red)' }}>
                        {fmt(sub.paidAmount)} <span style={{ fontSize: 10 }}>UZS</span>
                      </div>
                      {sub.remainingAmount > 0 && (
                        <div style={{ fontSize: 11, color: 'var(--red)', marginBottom: 2 }}>-{fmt(sub.remainingAmount)} left</div>
                      )}
                      <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>{METHOD_ICONS[sub.paymentMethod]}</div>
                    </div>
                  ) : (
                    <div style={{ color: c.dot, fontSize: 12 }}>
                      {status === 'overdue' ? t('overdue_cal') : t('noData_cal')}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
